import { FC, memo, useCallback } from "react";

import { handleFocus } from "@/utils/dom/handleFocus";

import {
    StyledCounter,
    StyledCounterDecrement,
    StyledCounterIncrement,
    StyledNumberInput,
} from "./Styled";

interface CounterProps {
    value: number;
    onChange: (value: number) => void;
    min?: number;
    max?: number;
}

export const Counter: FC<CounterProps> = memo(
    ({ value, onChange, min = 0, max = 100000 }) => {
        const handleIncrement = useCallback(() => {
            if (value >= max) return;
            onChange(value + 1);
        }, [value, max, onChange]);

        const handleDecrement = useCallback(() => {
            if (value < min) return;
            onChange(value - 1);
        }, [value, min, onChange]);

        return (
            <StyledCounter>
                <StyledCounterDecrement
                    onClick={handleDecrement}
                    disabled={value <= min}
                >
                    -
                </StyledCounterDecrement>
                <StyledNumberInput
                    size="large"
                    min={min}
                    max={max}
                    value={value}
                    onChange={(val) => {
                        onChange(Number(val ?? 0));
                    }}
                    onFocus={(e) => {
                        handleFocus(e);
                        e.target.inputMode = "numeric";
                    }}
                    parser={(val = "") => {
                        const numStr = val.replace(/\D/g, "");
                        return numStr === "" ? 0 : parseInt(numStr, 10);
                    }}
                    inputMode="numeric"
                />
                <StyledCounterIncrement
                    onClick={handleIncrement}
                    disabled={value >= max}
                >
                    +
                </StyledCounterIncrement>
            </StyledCounter>
        );
    },
);

Counter.displayName = "Counter";
